import {
  accountSearchColumns,
  boPhanSearchColumns,
  chiPhiKhongHopLeSearchColumns,
  hopDongSearchColumns,
  kheUocSearchColumns,
  lenhSanXuatSearchColumns,
  lyDoNhapXuatSearchColumns,
  paymentInstallmentSearchColumns,
  phiSearchColumns,
  QUERY_KEYS,
  vatTuSearchColumns,
  vuViecSearchColumns,
  warehouseSearchColumns
} from '@/constants';
import {
  AccountModel,
  BoPhan,
  ChiPhiKhongHopLeData,
  ChiTietPhieuNhapKho,
  DotThanhToan,
  HopDong,
  KheUoc,
  KhoHang,
  LenhSanXuat,
  NhapXuat,
  Phi,
  VatTu,
  VuViec
} from '@/types/schemas';
import {
  CellFieldV2 as CellField,
  ConversionQuantityFieldV2,
  DvtFieldV2,
  SearchFieldV2,
  SearchFieldV2 as SearchField
} from '@/components/custom/erp';
import { calculateMoney } from '@/features/ton-kho/phieu-nhap-kho/utils/calc-util';
import { Checkbox } from '@/components/ui/checkbox';

type CellParams = { row: ChiTietPhieuNhapKho; value?: unknown };
type LenhSanXuatLookup = LenhSanXuat & { ma_lsx?: string };

export const getWarehouseReceiptItemColumns = (
  onCellValueChange: (rowUuid: string | undefined, field: string, newValue: unknown) => void,
  fetchDetail: (params: string) => Promise<unknown>,
  hasInvoiceParams = false
) => {
  const disable = hasInvoiceParams;
  return [
    {
      field: 'ma_vt',
      headerName: 'Mã sản phẩm',
      width: 200,
      renderCell: (params: CellParams) => (
        <SearchField<VatTu>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VAT_TU}/`}
          searchColumns={vatTuSearchColumns}
          dialogTitle='Danh mục vật tư'
          columnDisplay='ma_vt'
          value={params.row.ma_vt_data?.ma_vt || ''}
          onRowSelection={async (row: VatTu) => {
            const data = await fetchDetail(row?.uuid || '');
            onCellValueChange(params.row.uuid, 'ma_vt_data', data);
          }}
          className='w-full'
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vt',
        resultField: 'ma_vt_data',
        detailEndpoint: QUERY_KEYS.VAT_TU
      }
    },
    {
      field: 'ten_vt',
      headerName: 'Tên sản phẩm',
      width: 220,
      renderCell: (params: CellParams) => (
        <CellField
          name='ten_vt'
          type='text'
          value={params.row.ma_vt_data?.ten_vt || ''}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'ten_vt', newValue)}
          disabled={disable}
        />
      ),
      nonWritable: true
    },
    {
      field: 'dvt',
      headerName: 'Đvt',
      width: 120,
      renderCell: (params: CellParams) => (
        <DvtFieldV2 row={params.row} onCellValueChange={onCellValueChange} disabled={disable} showConversionInEdit />
      ),
      nonWritable: true
    },
    {
      field: 'ma_kho',
      headerName: 'Mã kho',
      width: 150,
      renderCell: (params: CellParams) => (
        <SearchField<KhoHang>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHO_HANG}/`}
          searchColumns={warehouseSearchColumns}
          dialogTitle='Danh mục kho'
          columnDisplay='ma_kho'
          value={params.row.ma_kho_data?.ma_kho || params.row.ma_vt_data?.ma_kho_data?.ma_kho}
          onRowSelection={(row: KhoHang) => onCellValueChange(params.row.uuid, 'ma_kho_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_kho',
        resultField: 'ma_kho_data',
        detailEndpoint: QUERY_KEYS.KHO_HANG
      }
    },
    {
      field: 'so_luong',
      headerName: 'Số lượng',
      width: 130,
      renderCell: (params: CellParams) => (
        <ConversionQuantityFieldV2
          row={params.row}
          onCellValueChange={onCellValueChange}
          disabled={disable}
          showConversionInEdit
        />
      )
    },
    {
      field: 'pn_tb',
      headerName: 'Giá trung bình',
      width: 120,
      type: 'boolean',
      align: 'center',
      renderCell: (params: CellParams) => (
        <Checkbox
          name='pn_tb'
          checked={Boolean(params.row.pn_tb)}
          onCheckedChange={newValue => onCellValueChange(params.row.uuid, 'pn_tb', newValue)}
          disabled={disable}
        />
      ),
      nonWritable: true
    },
    {
      field: 'gia_nt',
      headerName: 'Giá VND',
      width: 160,
      renderCell: (params: CellParams) => (
        <CellField
          name='gia_nt'
          type='number'
          value={params.row.gia_nt || 0.0}
          disabled={params.row.pn_tb === true || disable}
          onValueChange={newValue => {
            onCellValueChange(params.row.uuid, 'gia_nt', newValue);
          }}
        />
      )
    },
    {
      field: 'tien_nt',
      headerName: 'Tiền VND',
      width: 160,
      renderCell: (params: CellParams) => (
        <CellField
          name='tien_nt'
          type='number'
          value={params.row.tien_nt || calculateMoney(params.row.gia_nt, params.row.so_luong)}
          onValueChange={newValue => onCellValueChange(params.row.uuid, 'tien_nt', newValue)}
          disabled={disable}
        />
      )
    },
    {
      field: 'tk_vt',
      headerName: 'Tk nợ',
      width: 150,
      renderCell: (params: CellParams) => (
        <SearchField<AccountModel>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}`}
          searchColumns={accountSearchColumns}
          dialogTitle='Danh mục tài khoản'
          columnDisplay='code'
          value={params.row.tk_vt_data?.code || ''}
          onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_vt_data', row)}
          disabled={disable}
        />
      ),
      nonWritable: true
    },
    {
      field: 'ma_nx',
      headerName: 'Lý do nhập',
      width: 150,
      renderCell: (params: CellParams) => (
        <SearchField<NhapXuat>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.LY_DO_NHAP_XUAT}`}
          searchColumns={lyDoNhapXuatSearchColumns}
          dialogTitle='Lý do nhập xuất'
          columnDisplay='ma_nx'
          value={params.row.ma_nx_data?.ma_nx || ''}
          onRowSelection={(row: NhapXuat) => onCellValueChange(params.row.uuid, 'ma_nx_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_nx',
        resultField: 'ma_nx_data',
        detailEndpoint: QUERY_KEYS.LY_DO_NHAP_XUAT
      }
    },
    {
      field: 'tk_du',
      headerName: 'Tk có',
      width: 150,
      renderCell: (params: CellParams) => (
        <SearchField<AccountModel>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.TAI_KHOAN}`}
          searchColumns={accountSearchColumns}
          dialogTitle='Danh mục tài khoản'
          columnDisplay='code'
          value={params.row.tk_du_data?.code || params.row.ma_nx_data?.tk_data?.code || ''}
          onRowSelection={(row: AccountModel) => onCellValueChange(params.row.uuid, 'tk_du_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'tk_du',
        resultField: 'tk_du_data',
        detailEndpoint: QUERY_KEYS.TAI_KHOAN
      }
    },
    {
      field: 'ma_bp',
      headerName: 'Bộ phận',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<BoPhan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.BO_PHAN}`}
          searchColumns={boPhanSearchColumns}
          dialogTitle='Danh mục bộ phận'
          columnDisplay='ma_bp'
          value={params.row.ma_bp_data?.ma_bp || ''}
          onRowSelection={(row: BoPhan) => onCellValueChange(params.row.uuid, 'ma_bp_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_bp',
        resultField: 'ma_bp_data',
        detailEndpoint: QUERY_KEYS.BO_PHAN
      }
    },
    {
      field: 'ma_vv',
      headerName: 'Vụ việc',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<VuViec>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.VU_VIEC}`}
          searchColumns={vuViecSearchColumns}
          dialogTitle='Danh mục vụ việc'
          columnDisplay='ma_vu_viec'
          value={params.row.ma_vv_data?.ma_vu_viec || params.row.ma_vv_data?.ma_vv}
          onRowSelection={(row: VuViec) => onCellValueChange(params.row.uuid, 'ma_vv_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_vv',
        resultField: 'ma_vv_data',
        detailEndpoint: QUERY_KEYS.VU_VIEC
      }
    },
    {
      field: 'ma_hd',
      headerName: 'Hợp đồng',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<HopDong>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.HOP_DONG}`}
          searchColumns={hopDongSearchColumns}
          dialogTitle='Danh mục hợp đồng'
          columnDisplay='ma_hd'
          value={params.row.ma_hd_data?.ma_hd || ''}
          onRowSelection={(row: HopDong) => onCellValueChange(params.row.uuid, 'ma_hd_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_hd',
        resultField: 'ma_hd_data',
        detailEndpoint: QUERY_KEYS.HOP_DONG
      }
    },
    {
      field: 'ma_dtt',
      headerName: 'Đợt thanh toán',
      width: 140,
      renderCell: (params: CellParams) => (
        <SearchField<DotThanhToan>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.DOT_THANH_TOAN}`}
          searchColumns={paymentInstallmentSearchColumns}
          dialogTitle='Danh mục đợt thanh toán'
          columnDisplay='ma_dtt'
          value={params.row.ma_dtt_data?.ma_dtt || ''}
          onRowSelection={(row: DotThanhToan) => onCellValueChange(params.row.uuid, 'ma_dtt_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_dtt',
        resultField: 'ma_dtt_data',
        detailEndpoint: QUERY_KEYS.DOT_THANH_TOAN
      }
    },
    {
      field: 'ma_ku',
      headerName: 'Khế ước',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<KheUoc>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.KHE_UOC}`}
          searchColumns={kheUocSearchColumns}
          dialogTitle='Danh mục khế ước'
          columnDisplay='ma_ku'
          value={params.row.ma_ku_data?.ma_ku || ''}
          onRowSelection={(row: KheUoc) => onCellValueChange(params.row.uuid, 'ma_ku_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_ku',
        resultField: 'ma_ku_data',
        detailEndpoint: QUERY_KEYS.KHE_UOC
      }
    },
    {
      field: 'ma_phi',
      headerName: 'Phí',
      width: 120,
      renderCell: (params: CellParams) => (
        <SearchField<Phi>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.PHI}`}
          searchColumns={phiSearchColumns}
          dialogTitle='Danh mục phí'
          columnDisplay='ma_phi'
          value={params.row.ma_phi_data?.ma_phi || ''}
          onRowSelection={(row: Phi) => onCellValueChange(params.row.uuid, 'ma_phi_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_phi',
        resultField: 'ma_phi_data',
        detailEndpoint: QUERY_KEYS.PHI
      }
    },
    {
      field: 'ma_lsx',
      headerName: 'Lệnh sản xuất',
      width: 195,
      renderCell: (params: CellParams) => (
        <SearchFieldV2<LenhSanXuatLookup>
          searchEndpoint={`/${QUERY_KEYS.LENH_SAN_XUAT}`}
          searchColumns={lenhSanXuatSearchColumns}
          dialogTitle='Danh mục lệnh sản xuất'
          value={params.row.ma_lsx_data?.so_ct || ''}
          onRowSelection={(row: LenhSanXuatLookup) => onCellValueChange(params.row.uuid, 'ma_lsx_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_lsx',
        resultField: 'ma_lsx_data',
        detailEndpoint: QUERY_KEYS.LENH_SAN_XUAT
      }
    },
    {
      field: 'ma_cp0',
      headerName: 'C/p không h/lệ',
      width: 140,
      renderCell: (params: CellParams) => (
        <SearchField<ChiPhiKhongHopLeData>
          type='text'
          searchEndpoint={`/${QUERY_KEYS.CHI_PHI_KHONG_HOP_LE}/`}
          searchColumns={chiPhiKhongHopLeSearchColumns}
          dialogTitle='Danh mục chi phí không hợp lệ'
          columnDisplay='ma_cp_khl'
          value={params.row.ma_cp0_data?.ma_cpkhl || params.row.ma_cp0_data?.ma_cp}
          onRowSelection={(row: ChiPhiKhongHopLeData) => onCellValueChange(params.row.uuid, 'ma_cp0_data', row)}
          disabled={disable}
        />
      ),
      pasteConfig: {
        lookupKey: 'ma_cp0',
        resultField: 'ma_cp0_data',
        detailEndpoint: QUERY_KEYS.CHI_PHI_KHONG_HOP_LE
      }
    },
    {
      field: 'so_ct_sx1',
      headerName: 'Số lệnh SX',
      width: 150,
      renderCell: (params: CellParams) => params.row.ma_lsx_data?.ma_lsx,
      nonWritable: true
    },
    {
      field: 'line_sx1',
      headerName: 'Dòng lệnh SX',
      width: 120,
      renderCell: (params: CellParams) => (
        <CellField name='line_sx1' type='text' value={params.row.line_sx1 || ''} disabled={true} />
      ),
      nonWritable: true
    }
  ];
};
